"use client"

import * as React from "react"
import { cn } from "../../lib/utils"
import { cva, VariantProps } from "class-variance-authority"
import { Eye, EyeOff } from "lucide-react"
import { Button } from "./button"
import { IMaskInput, IMaskInputProps } from "react-imask"

export const inputVariants = cva(
    "flex w-full border border-input bg-transparent rounded text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
    {
        variants: {
            variant: {
                default:
                    "bg-white placeholder:text-muted-foreground/60 placeholder:text-sm focus-visible:border-primary",
                destructive:
                    "bg-destructive/10 focus-visible:border-destructive",
            },
            size: {
                default: "h-14 px-3 py-1 text-base ",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
)

const inputIconClassName =
    "absolute z-10 text-muted-foreground w-5 h-5 flex items-center justify-center"

const inputRightIconClassName = cn(inputIconClassName, "right-4")

export type InputProps = Omit<React.HTMLProps<HTMLInputElement>, "size"> &
    VariantProps<typeof inputVariants> & {
        mask?: IMaskInputProps<HTMLInputElement>
        wrapperClassName?: string
        rightIcon?: React.ReactNode
        leftIcon?: React.ReactNode
    }

const PasswordToggle = ({
    showPassword,
    setShowPassword,
}: {
    showPassword: boolean
    setShowPassword: (show: boolean) => void
}) => (
    <Button
        variant={"ghost"}
        size={"icon"}
        className={cn(inputRightIconClassName, "cursor-pointer")}
        type="button"
        onClick={() => setShowPassword(!showPassword)}
        asChild
    >
        {showPassword ? <EyeOff className="" /> : <Eye className="" />}
    </Button>
)

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            className,
            type = "text",
            variant,
            size,
            mask,
            wrapperClassName,
            rightIcon,
            leftIcon,
            onChange,
            value,
            ...props
        },
        ref
    ) => {
        const isPasswordField = type === "password"
        const [showPassword, setShowPassword] = React.useState(false)

        // Calculate classes once
        const inputClassName = cn(
            inputVariants({ size, variant }),
            {
                "pr-8 md:pr-9": isPasswordField || rightIcon,
                "pl-8 md:pl-9": leftIcon,
            },
            className
        )

        // Determine the effective input type
        const effectiveType = isPasswordField
            ? showPassword
                ? "text"
                : "password"
            : type

        return (
            <div
                className={cn(
                    "w-full relative flex items-center",
                    wrapperClassName
                )}
            >
                {/* TODO: move this leftIcon Functionality in group */}
                {leftIcon && (
                    <div className={cn(inputIconClassName, "left-2 lg:left-3")}>
                        {leftIcon}
                    </div>
                )}
                <IMaskInput
                    {...mask}
                    onChange={(e) => {
                        if (onChange) {
                            // Pass the raw event to the parent component
                            onChange(e)
                        }
                        if (mask?.onAccept) {
                            // This will be handled by the mask directly
                        }
                    }}
                    onBlur={props.onBlur}
                    onFocus={props.onFocus}
                    onClick={props.onClick}
                    onKeyDown={props.onKeyDown}
                    value={(value as string) || ""}
                    placeholder={props.placeholder}
                    className={inputClassName}
                    style={props.style}
                    disabled={props.disabled}
                    inputRef={ref}
                    type={effectiveType}
                    unmask={mask?.unmask || false}
                    id={props.id}
                    aria-label={props["aria-label"]}
                    name={props.name}
                    autoComplete={props.autoComplete}
                    required={props.required}
                    readOnly={props.readOnly}
                    maxLength={props.maxLength}
                    minLength={props.minLength}
                    pattern={props.pattern}
                    title={props.title}
                    tabIndex={props.tabIndex}
                    role={props.role}
                    aria-describedby={props["aria-describedby"]}
                    aria-invalid={props["aria-invalid"]}
                    aria-required={props["aria-required"]}
                    aria-disabled={props["aria-disabled"]}
                />
                {!rightIcon && isPasswordField && (
                    <PasswordToggle
                        showPassword={showPassword}
                        setShowPassword={setShowPassword}
                    />
                )}
                {rightIcon && (
                    <div
                        className={cn(inputIconClassName, "right-2 md:right-4")}
                    >
                        {rightIcon}
                    </div>
                )}
            </div>
        )
    }
)

Input.displayName = "Input"

export { Input }
